import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/bi/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileText, Plus, Copy, Trash2, Search, ScrollText, Users, MessagesSquare, LayoutDashboard, Loader2 } from "lucide-react";

const TEMPLATES = [
  { key: "blank", name: "Blank report", desc: "Start from an empty page", elements: [] },
  {
    key: "sales", name: "Sales report", desc: "Title, KPIs, chart and detailed table",
    elements: [
      { type: "heading", config: { text: "Sales Report", level: 1 } },
      { type: "text", config: { text: "Prepared by {preparedBy} on {date}", size: 10, color: "#6B7280" } },
      { type: "divider", config: {} },
      { type: "kpi", config: {} },
      { type: "chart", config: { chart_type: "column", height: 300 } },
      { type: "table", config: { totals: true, subtotals: true, zebra: true } },
    ],
  },
  {
    key: "executive", name: "Executive summary", desc: "One-pager: headline KPIs + two charts",
    elements: [
      { type: "heading", config: { text: "Executive Summary — {date}", level: 1 } },
      { type: "kpi", config: {} },
      { type: "spacer", config: { height: 12 } },
      { type: "chart", config: { chart_type: "line", height: 280 } },
      { type: "chart", config: { chart_type: "donut", height: 280 } },
    ],
  },
  {
    key: "statement", name: "Grouped statement", desc: "Table grouped with subtotals per section",
    elements: [
      { type: "heading", config: { text: "Statement", level: 1 } },
      { type: "text", config: { text: "Period: {date}", size: 10, color: "#6B7280" } },
      { type: "table", config: { totals: true, subtotals: true, zebra: false } },
    ],
  },
];

export default function Reports() {
  const nav = useNavigate();
  const [reports, setReports] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [tpl, setTpl] = useState("blank");
  const [fromDashOpen, setFromDashOpen] = useState(false);

  const load = () => api.get("/report-designs").then(({ data }) => setReports(data)).catch(() => setReports([]));
  const loadTpls = () => api.get("/report-templates").then(({ data }) => setTemplates(data)).catch(() => setTemplates([]));
  useEffect(() => { load(); loadTpls(); }, []);

  const create = async () => {
    // Built-in template
    const b = TEMPLATES.find((x) => x.key === tpl);
    if (b) {
      try {
        const { data } = await api.post("/report-designs", {
          name: newName || b.name,
          elements: b.elements.map((e, i) => ({ id: `el_${Date.now()}_${i}`, ...e })),
        });
        nav(`/reports/${data.id}`);
      } catch { toast.error("Could not create report"); }
      return;
    }
    // Team template — carry over layout + parameters + theme
    const t = templates.find((x) => x.id === tpl);
    if (!t) return;
    try {
      const { data } = await api.post("/report-designs", {
        name: newName || `${t.name}`,
        elements: (t.elements || []).map((e, i) => ({ ...e, id: `el_${Date.now()}_${i}` })),
        parameters: t.parameters || [],
        page: t.page, header: t.header, footer: t.footer,
        watermark: t.watermark, theme: t.theme,
      });
      nav(`/reports/${data.id}`);
    } catch { toast.error("Could not create report"); }
  };
  const duplicate = async (id) => { try { const { data } = await api.post(`/report-designs/${id}/duplicate`); toast.success("Duplicated"); nav(`/reports/${data.id}`); } catch { toast.error("Failed"); } };
  const remove = async (id) => { try { await api.delete(`/report-designs/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const removeTpl = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this team template? Existing reports won't be affected.")) return;
    try { await api.delete(`/report-templates/${id}`); toast.success("Template deleted"); loadTpls(); if (tpl === id) setTpl("blank"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const shown = (reports || []).filter((r) => !q || (r.name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <AppShell title="Reports">
      <div className="p-6 max-w-5xl mx-auto space-y-5">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-display text-2xl text-[color:var(--ink)] flex items-center gap-2"><ScrollText className="w-6 h-6 text-[color:var(--brand-ocean)]" />Report Maker</h1>
          <span className="flex-1" />
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[color:var(--ink-3)]" />
            <Input data-testid="reports-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reports…" className="pl-8 w-56 bg-[color:var(--surface)]" />
          </div>
          <Button data-testid="report-from-dashboard-btn" variant="outline" onClick={() => setFromDashOpen(true)} className="bg-[color:var(--surface-2)]"><LayoutDashboard className="w-4 h-4 mr-1.5" />From dashboard</Button>
          <Button data-testid="report-create-btn" onClick={() => { setNewName(""); setTpl("blank"); setCreateOpen(true); }} className="bg-[color:var(--brand-ocean)] text-white"><Plus className="w-4 h-4 mr-1.5" />New report</Button>
        </div>
        <p className="text-sm text-[color:var(--ink-3)]">Design pixel-clean, printable business documents — bound to your datasets, exportable to PDF, Excel, Word & HTML, and deliverable on a schedule.</p>
        <ReactionAnalyticsCard onOpen={(reportId) => nav(`/reports/${reportId}`)} />
        {reports === null ? (
          <div className="text-sm text-[color:var(--ink-3)] py-16 text-center">Loading…</div>
        ) : shown.length === 0 ? (
          <div data-testid="reports-empty" className="text-center py-20 border border-dashed border-[color:var(--glass-border)] rounded-2xl">
            <FileText className="w-10 h-10 mx-auto text-[color:var(--ink-3)] opacity-50" />
            <div className="mt-3 text-[color:var(--ink-2)] font-medium">No reports yet</div>
            <div className="text-sm text-[color:var(--ink-3)] mt-1">Create your first report from a template.</div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {shown.map((r) => (
              <div key={r.id} data-testid={`report-card-${r.id}`} onClick={() => nav(`/reports/${r.id}`)}
                className="group cursor-pointer rounded-2xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-4 hover:shadow-[var(--shadow-md)] transition-shadow">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[color:var(--brand-ocean)]/10 flex items-center justify-center shrink-0"><FileText className="w-4.5 h-4.5 text-[color:var(--brand-ocean)]" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[color:var(--ink)] truncate">{r.name}</div>
                    <div className="text-[11px] text-[color:var(--ink-3)] mt-0.5">by {r.owner_name || "—"} · {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : ""}</div>
                  </div>
                </div>
                <div className="flex justify-end gap-1 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`report-duplicate-${r.id}`} title="Duplicate" onClick={(e) => { e.stopPropagation(); duplicate(r.id); }} className="p-1.5 rounded-lg text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)]"><Copy className="w-4 h-4" /></button>
                  <button data-testid={`report-delete-${r.id}`} title="Delete" onClick={(e) => { e.stopPropagation(); remove(r.id); }} className="p-1.5 rounded-lg text-[color:var(--ink-3)] hover:text-[color:var(--danger)] hover:bg-[color:var(--surface-2)]"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">New report</DialogTitle></DialogHeader>
          <Input data-testid="report-name-input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Report name" className="bg-[color:var(--surface)]" />
          <div className="space-y-2 max-h-[46vh] overflow-y-auto pr-1">
            <div className="text-[10px] font-semibold text-[color:var(--ink-3)] uppercase tracking-wide pt-1">Built-in</div>
            {TEMPLATES.map((t) => (
              <label key={t.key} className={`flex items-start gap-3 p-2.5 rounded-xl border cursor-pointer ${tpl === t.key ? "border-[color:var(--brand-ocean)] bg-[color:var(--brand-ocean)]/5" : "border-[color:var(--glass-border)]"}`}>
                <input type="radio" name="tpl" checked={tpl === t.key} onChange={() => setTpl(t.key)} className="mt-1" data-testid={`report-tpl-${t.key}`} />
                <span><span className="text-sm font-medium text-[color:var(--ink)]">{t.name}</span><br /><span className="text-xs text-[color:var(--ink-3)]">{t.desc}</span></span>
              </label>
            ))}
            {templates.length > 0 && (
              <>
                <div className="text-[10px] font-semibold text-[color:var(--ink-3)] uppercase tracking-wide pt-2 flex items-center gap-1.5">
                  <Users className="w-3 h-3" />Team templates
                </div>
                {templates.map((t) => (
                  <label key={t.id} data-testid={`report-tpl-team-${t.id}`} className={`flex items-start gap-3 p-2.5 rounded-xl border cursor-pointer group ${tpl === t.id ? "border-[color:var(--brand-ocean)] bg-[color:var(--brand-ocean)]/5" : "border-[color:var(--glass-border)]"}`}>
                    <input type="radio" name="tpl" checked={tpl === t.id} onChange={() => setTpl(t.id)} className="mt-1" />
                    <span className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-[color:var(--ink)]">{t.name}</span>
                      <br />
                      <span className="text-xs text-[color:var(--ink-3)]">
                        {t.description || `${(t.elements || []).length} elements`} · by {t.owner_name || "team"}
                      </span>
                    </span>
                    <button data-testid={`report-tpl-delete-${t.id}`} onClick={(e) => removeTpl(t.id, e)} title="Delete template"
                      className="opacity-0 group-hover:opacity-100 p-1 rounded text-[color:var(--ink-3)] hover:text-[color:var(--danger)]">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </label>
                ))}
              </>
            )}
          </div>
          <DialogFooter><Button data-testid="report-create-confirm" onClick={create} className="bg-[color:var(--brand-ocean)] text-white">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <FromDashboardDialog open={fromDashOpen} onOpenChange={setFromDashOpen} onCreated={(r) => nav(`/reports/${r.id}`)} />
    </AppShell>
  );
}

// Item 23: convert an existing dashboard (or a single tab) into a report design.
function FromDashboardDialog({ open, onOpenChange, onCreated }) {
  const [dashboards, setDashboards] = useState(null);
  const [sel, setSel] = useState("");
  const [tabId, setTabId] = useState("all");
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setSel(""); setDetail(null); setTabId("all"); setSearch("");
    api.get("/dashboards").then(({ data }) => setDashboards(data || [])).catch(() => setDashboards([]));
  }, [open]);
  useEffect(() => {
    if (!sel) { setDetail(null); return; }
    setTabId("all");
    api.get(`/dashboards/${sel}`).then(({ data }) => setDetail(data)).catch(() => setDetail(null));
  }, [sel]);

  const convert = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      const { data } = await api.post("/report-designs/from-dashboard", {
        dashboard_id: sel, ...(tabId && tabId !== "all" ? { tab_id: tabId } : {}),
      });
      toast.success(data.skipped_widgets ? `Report created — ${data.skipped_widgets} widget(s) could not be converted` : "Report created from dashboard");
      onOpenChange(false);
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Conversion failed"); }
    setBusy(false);
  };

  const list = (dashboards || []).filter((d) => !search || (d.name || "").toLowerCase().includes(search.toLowerCase()));
  const tabs = (detail?.tabs || []).filter((t) => t && t.id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="report-from-dashboard-dialog" className="max-w-lg" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><LayoutDashboard className="w-4 h-4" />Create report from dashboard</DialogTitle></DialogHeader>
        <p className="text-xs text-[color:var(--ink-3)]">Widgets keep their dataset, fields, filters and reading order. The dashboard itself is never modified.</p>
        <Input data-testid="report-from-dashboard-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search dashboards…" className="h-8 text-xs bg-[color:var(--surface-2)]" />
        <div data-testid="report-from-dashboard-list" className="max-h-[260px] overflow-y-auto space-y-1 pr-1">
          {dashboards === null ? <div className="text-xs text-[color:var(--ink-3)] py-4 text-center">Loading…</div>
            : list.length === 0 ? <div className="text-xs text-[color:var(--ink-3)] py-4 text-center">No dashboards you can export.</div>
              : list.map((d) => (
                <label key={d.id} data-testid={`report-from-dashboard-item-${d.id}`}
                  className={`flex items-center gap-2 p-2 rounded-xl border cursor-pointer text-xs ${sel === d.id ? "border-[color:var(--brand-ocean)] bg-[color:var(--brand-ocean)]/5" : "border-[color:var(--glass-border)] hover:bg-[color:var(--surface-2)]"}`}>
                  <input type="radio" name="fromdash" checked={sel === d.id} onChange={() => setSel(d.id)} />
                  <span className="flex-1 truncate text-[color:var(--ink)]">{d.name}</span>
                  <span className="text-[10px] text-[color:var(--ink-3)]">{d.status || "development"}</span>
                </label>
              ))}
        </div>
        {tabs.length > 1 && (
          <label className="flex items-center gap-2 text-xs text-[color:var(--ink-2)]">
            Pages
            <select data-testid="report-from-dashboard-tab" value={tabId} onChange={(e) => setTabId(e.target.value)}
              className="h-7 text-xs rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-2">
              <option value="all">All tabs (page break between)</option>
              {tabs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
        )}
        <DialogFooter>
          <Button data-testid="report-from-dashboard-confirm" disabled={!sel || busy} onClick={convert} className="bg-[color:var(--brand-ocean)] text-white">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}Convert to report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReactionAnalyticsCard({ onOpen }) {
  const [items, setItems] = useState(null);
  const [days, setDays] = useState(7);
  React.useEffect(() => {
    api.get(`/report-comments/reactions/top?days=${days}&limit=5`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  }, [days]);
  if (!items || items.length === 0) return null;
  return (
    <div data-testid="reactions-analytics" className="glass-card p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <MessagesSquare className="w-4 h-4 text-[color:var(--brand-ocean)]" />
          <h3 className="font-display text-sm text-[color:var(--ink)]">Top-reacted comments</h3>
        </div>
        <select data-testid="reactions-analytics-window" value={days} onChange={(e) => setDays(parseInt(e.target.value))}
          className="h-7 rounded-md border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-2 text-[11px]">
          {[7, 14, 30].map((d) => <option key={d} value={d}>Last {d} days</option>)}
        </select>
      </div>
      <div className="space-y-1.5">
        {items.map((c) => (
          <button key={c.id} data-testid={`reactions-analytics-item-${c.id}`}
            onClick={() => onOpen?.(c.report_id)}
            className="w-full flex items-start gap-2 text-left p-2 rounded-lg bg-[color:var(--surface)] border border-[color:var(--glass-border)] hover:border-[color:var(--brand-ocean)] transition-colors">
            <span className="min-w-[36px] h-6 px-1.5 rounded-full flex items-center justify-center bg-[color:var(--brand-ocean)]/10 text-[color:var(--brand-ocean)] text-[11px] font-bold tabular-nums">
              {c.total_reactions}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-[color:var(--ink-3)] truncate">
                <span className="font-semibold text-[color:var(--ink)]">{c.report_name}</span>
                {" · "}<span>{c.user_name}</span>
                {c.is_guest && <span className="ml-1 text-[9px] uppercase text-[color:var(--ink-3)]">guest</span>}
              </div>
              <div className="text-xs text-[color:var(--ink-2)] truncate">“{c.text}”</div>
              <div className="text-[10px] text-[color:var(--ink-3)] mt-0.5 flex gap-2 flex-wrap">
                {Object.entries(c.by_kind || {}).map(([k, n]) => (
                  <span key={k} className="tabular-nums">{k} {n}</span>
                ))}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

